import React, { useState } from 'react';
import { Card, Input, Button, message, Spin, Typography, Space, Collapse, Upload } from 'antd';
import { FilePdfOutlined, EyeOutlined, DownloadOutlined, PictureOutlined, InboxOutlined, SyncOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useTheme } from '../../context/ThemeContext';

const { TextArea } = Input;
const { Title, Text } = Typography;
const { Panel } = Collapse;

const ACCEPT_IMAGES = '.png,.jpg,.jpeg,.gif,.bmp,.pdf';
const MAX_FILE_SIZE_MB = 10;

const CreatePdf = () => {
  const { darkMode } = useTheme();
  const [prompt, setPrompt] = useState('');
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [compileLoading, setCompileLoading] = useState(false);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [latex, setLatex] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL || '';

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      message.warning('Lütfen bir istek yazın (örn: logo ortada, sağda hello yazsın).');
      return;
    }
    setLoading(true);
    setPdfBase64(null);
    setLatex(null);
    try {
      const formData = new FormData();
      formData.append('prompt', prompt.trim());
      fileList.forEach((item) => {
        if (item.originFileObject) {
          formData.append('files', item.originFileObject);
        }
      });
      const { data } = await axios.post(`${apiUrl}/create-pdf/generate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.success) {
        setPdfBase64(data.pdf_base64);
        setLatex(data.latex);
        message.success('PDF oluşturuldu.');
      } else {
        message.error('PDF oluşturulamadı.');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      message.error(typeof detail === 'string' ? detail : 'PDF oluşturulurken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!pdfBase64) return;
    const blob = base64ToBlob(pdfBase64, 'application/pdf');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'latex-dokuman.pdf';
    a.click();
    URL.revokeObjectURL(url);
    message.success('İndirme başlatıldı.');
  };

  const handleRecompile = async () => {
    if (!latex || !latex.trim()) {
      message.warning('LaTeX kodu boş.');
      return;
    }
    setCompileLoading(true);
    try {
      const formData = new FormData();
      formData.append('latex', latex.trim());
      fileList.forEach((item) => {
        if (item.originFileObject) {
          formData.append('files', item.originFileObject);
        }
      });
      const { data } = await axios.post(`${apiUrl}/create-pdf/compile`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.success) {
        setPdfBase64(data.pdf_base64);
        if (data.latex != null) setLatex(data.latex);
        message.success('Önizleme güncellendi.');
      } else {
        message.error('PDF derlenemedi.');
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      const msg = typeof detail === 'string' ? detail : 'Derleme hatası.';
      message.error({ content: msg, duration: 8 });
    } finally {
      setCompileLoading(false);
    }
  };

  const uploadProps = {
    accept: ACCEPT_IMAGES,
    multiple: true,
    fileList,
    beforeUpload: (file) => {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        message.error(`${file.name} ${MAX_FILE_SIZE_MB} MB sınırını aşıyor.`);
        return Upload.LIST_IGNORE;
      }
      setFileList((prev) => [...prev, { uid: file.uid, name: file.name, originFileObject: file }]);
      return false;
    },
    onRemove: (file) => {
      setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
    },
  };

  const pdfDataUrl = pdfBase64 ? `data:application/pdf;base64,${pdfBase64}` : null;

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <FilePdfOutlined />
            <span>PDF Oluştur</span>
          </Space>
        }
        style={{ maxWidth: 1500, maxHeight: 2000, margin: '0 auto' }}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          İstediğiniz sayfa düzenini yazın. Resim ekleyecekseniz yükleyip metindeki konumunu belirtin.
        </Text>
        <TextArea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Örn: Logo ortada, sağ üstte 'Merhaba' yazsın. Altında madde işaretli 3 satır olsun."
          rows={10}
          style={{ marginBottom: 16 }}
        />
        <div style={{ marginBottom: 16 }}>
          <Upload {...uploadProps} maxCount={10}>
            <Button icon={<InboxOutlined />}>Resim Seç</Button>
          </Upload>
          {fileList.length > 0 && (
            <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
              {fileList.length} dosya seçildi. Metinde &quot;ilk resim ortada&quot;, &quot;logoyu sağ üste koy&quot; gibi konum belirtin.
            </Text>
          )}
        </div>
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={handleGenerate}
            loading={loading}
          >
            Önizleme
          </Button>
          {pdfBase64 && (
            <Button icon={<DownloadOutlined />} onClick={handleDownload}>
              İndir
            </Button>
          )}
        </Space>

        {loading && (
          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <Spin size="large" tip="LaTeX üretiliyor ve PDF derleniyor..." />
          </div>
        )}

        {!loading && pdfDataUrl && (
          <div style={{ marginTop: 24 }}>
            <Title level={5}>Önizleme</Title>
            <div
              style={{
                border: `1px solid ${darkMode ? '#434343' : '#d9d9d9'}`,
                borderRadius: 8,
                overflow: 'hidden',
                minHeight: 480,
              }}
            >
              <iframe
                title="PDF önizleme"
                src={pdfDataUrl}
                width="100%"
                height="480"
                style={{ border: 'none' }}
              />
            </div>
            {latex && (
              <Collapse defaultActiveKey={['1']} style={{ marginTop: 16 }}>
                <Panel
                  header={
                    <Space>
                      <span>LaTeX kodu</span>
                      <Button
                        type="primary"
                        size="small"
                        icon={<SyncOutlined />}
                        onClick={(e) => { e.stopPropagation(); handleRecompile(); }}
                        loading={compileLoading}
                      >
                        Güncelle
                      </Button>
                    </Space>
                  }
                  key="1"
                >
                  <TextArea
                    value={latex}
                    onChange={(e) => setLatex(e.target.value)}
                    spellCheck={false}
                    style={{
                      fontFamily: 'monospace',
                      fontSize: 12,
                      minHeight: 280,
                      maxHeight: 400,
                      background: darkMode ? '#1f1f1f' : '#f5f5f5',
                    }}
                  />
                </Panel>
              </Collapse>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

function base64ToBlob(base64, mimeType) {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}

export default CreatePdf;
